/**
 * The entry backdrop shared by Login (12:311) and Country Picker (37:6397): navy,
 * a radial vignette, and four blurred ribbons rotated 14°.
 *
 * The geometry lives in `.crf-entry-*` in `app.css`, where the gradients can cite
 * the exact node values and reference the token variables via `color-mix()`. This
 * component is only the DOM those classes need.
 */

import type { ReactNode } from 'react';

export function EntryBackdrop({ children }: { children: ReactNode }): React.JSX.Element {
  return (
    <div className="crf-entry-backdrop flex min-h-dvh items-center justify-center p-6">
      <div aria-hidden="true" className="crf-entry-ribbon crf-entry-ribbon--back" />
      <div aria-hidden="true" className="crf-entry-ribbon crf-entry-ribbon--mid" />
      <div aria-hidden="true" className="crf-entry-ribbon crf-entry-ribbon--thin" />
      <div aria-hidden="true" className="crf-entry-ribbon crf-entry-ribbon--brand" />
      <div className="relative z-10 w-full">{children}</div>
    </div>
  );
}

/**
 * The G1 letterforms alone — no tile, no colour of its own.
 *
 * ⚠ ONE DRAWING, THREE PLACES, AND THIS IS THE WEB'S COPY OF IT. The path is
 * `apps/mobile/assets/brand/g1-mark.svg` on its 392 × 300 authoring viewBox,
 * which is also what `apps/mobile/lib/src/design/brand_mark.dart` transcribes
 * into a Flutter `Path`. If the artwork is ever re-cut, re-cut the SVG and copy
 * from it — do not nudge one of the three and leave the others behind.
 *
 * ⚠ `currentColor`, NOT A HEX, AND THE TEST DEPENDS ON IT.
 * `shell/sidebar-brand.test.tsx` asserts the rendered sidebar "carries no
 * literal colour — the tile is a token, not a hex". This component is rendered
 * inside that sidebar, so a `fill="#ed1c24"` here would fail that assertion on
 * its own terms. Every caller sets the colour with a text token instead.
 *
 * `aria-hidden` because every caller already labels the thing it sits in — a
 * mark announced twice is worse than a mark announced once.
 */
export function G1Glyph({ className }: { className?: string }): React.JSX.Element {
  return (
    <svg viewBox="0 0 392 300" aria-hidden="true" fill="currentColor" className={className}>
      <path d="M299.88 144.00A150.00 150.00 0 1 1 240.26 30.20L201.49 68.97A96.00 96.00 0 1 0 233.14 198.00L146.00 198.00L200.00 144.00ZM340.00 4.00L292.00 52.00L323.99 83.99L340.00 67.98L340.00 296.00L392.00 296.00L392.00 4.00Z" />
    </svg>
  );
}

/**
 * The Global One mark: `brand/primary` square, `radius` 10, the white G1.
 *
 * ⚠ THIS TILE IS `bg-brand` (#da2828) AND THE SIDEBAR'S IS `bg-brand-tile`
 * (#ed1c24). THE TWO REDS ARE PRE-EXISTING AND ARE DELIBERATELY LEFT ALONE.
 * `app.css:96-108` records why they were kept apart — collapsing them would
 * drag this component's normal-size labels from 4.86:1 to 4.38:1 — and picking
 * one is a design decision with a measured contrast consequence, not a rename.
 * It is filed as an owner decision. The rename did not resolve it and must not
 * be read as having resolved it.
 *
 * The mark's SIZE is a legibility floor, not a contrast one: white on either
 * red clears WCAG 1.4.11's 3:1 graphical-object bar at any size, but this
 * drawing loses its counter below 16px of mark height. 40px tile → 28px mark →
 * 21.4px tall; 28px tile → 22px mark → 16.8px tall. Both clear it; do not
 * shrink either.
 */
export function G1Mark({ size = 40 }: { size?: 28 | 40 }): React.JSX.Element {
  return (
    <span
      aria-label="Global One"
      role="img"
      className={
        size === 28
          ? 'inline-flex size-7 items-center justify-center rounded-sm bg-brand text-fg-inverse'
          : 'inline-flex size-10 items-center justify-center rounded-[10px] bg-brand text-fg-inverse'
      }
    >
      <G1Glyph className={size === 28 ? 'w-[22px]' : 'w-7'} />
    </span>
  );
}
